import {config} from "@commons/config.instance";
import axios from "axios";
import {AccumuloRecord} from "@commons/mapping/AccumuloRecord";
import {GiustificativoRecord, IdTabellaGenericaAzienda} from "@commons/mapping/GiustificativoRecord";
import {Views} from "@commons/Views";
import {addBaseQueryParams, addQueryParams, BaseQueryParams} from "@commons/utils/APIParams";
import {CentroDiCostoRecord} from "@commons/mapping/CentroDiCostoRecord";
import {StatusGiustificativoRecord} from "@commons/mapping/StatusGiustificativoRecord";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {TabellaArrotondamentiRecord} from "@commons/mapping/TabellaArrotondamentiRecord";
import {ArrotondamentiRangeRecord} from "@commons/mapping/ArrotondamentiRangeRecord";
import {FasciaOrariaWeekRecord} from "@commons/mapping/FasciaOrariaWeekRecord";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";

export interface AziendaParams {
    idAziendaTabelle: number,
    view?: Views,
    token?: string,
}

interface ULParams extends Partial<BaseQueryParams> {
    idAziendaMaster: number,
    filter_codAzienda?: number
    filter_codFiliale?: string
    filter_denominazione?: string
    filter_unitaLocale?: string
}

export const listUnitaLocali = (params: ULParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    if (!params.view)
        queryParams.push(`view=Minimal`);
    if (params.filter_codAzienda)
        queryParams.push(`filter_codAzienda=${params.filter_codAzienda}`);
    if (params.filter_codFiliale)
        queryParams.push(`filter_codFiliale=${params.filter_codFiliale}`);
    if (params.filter_denominazione)
        queryParams.push(`filter_denominazione=${params.filter_denominazione}`);
    if (params.filter_unitaLocale)
        queryParams.push(`filter_unitaLocale=${params.filter_unitaLocale}`);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaMaster}/ul?${addQueryParams(queryParams)}`
    return axios<AziendaRecord[]>(url).then(res => res.data)
}

export interface MapProps {
    ordinal: number,
    url: IdTabellaGenericaAzienda,
    descrizione: string,
};
export const mapTabelle: Record<IdTabellaGenericaAzienda, MapProps> = {
    CDIC: {ordinal: 0, url: 'CDIC', descrizione: 'Centro di costo'},
    MANSIONE: {ordinal: 18, url: 'MANSIONE', descrizione: 'Mansione'},
    GRUPPO_01: {ordinal: 21, url: 'GRUPPO_01', descrizione: 'Gruppo 1'},
    GRUPPO_02: {ordinal: 22, url: 'GRUPPO_02', descrizione: 'Gruppo 2'},
    GRUPPO_03: {ordinal: 23, url: 'GRUPPO_03', descrizione: 'Gruppo 3'},
    GRUPPO_04: {ordinal: 24, url: 'GRUPPO_04', descrizione: 'Gruppo 4'},
    GRUPPO_05: {ordinal: 25, url: 'GRUPPO_05', descrizione: 'Gruppo 5'},
    GRUPPO_06: {ordinal: 26, url: 'GRUPPO_06', descrizione: 'Gruppo 6'},
    GRUPPO_07: {ordinal: 27, url: 'GRUPPO_07', descrizione: 'Gruppo 7'},
    GRUPPO_08: {ordinal: 28, url: 'GRUPPO_08', descrizione: 'Gruppo 8'},
    GRUPPO_09: {ordinal: 29, url: 'GRUPPO_09', descrizione: 'Gruppo 9'},
    GRUPPO_10: {ordinal: 30, url: 'GRUPPO_10', descrizione: 'Gruppo 10'},
    GRUPPO_11: {ordinal: 31, url: 'GRUPPO_11', descrizione: 'Gruppo 11'},
    GRUPPO_12: {ordinal: 32, url: 'GRUPPO_12', descrizione: 'Gruppo 12'},
    PROF_RETRIBUTIVO: {ordinal: 12, url: 'PROF_RETRIBUTIVO', descrizione: 'Profilo retributivo'},
    STATUS: {ordinal: 10, url: 'STATUS', descrizione: 'Status'},
}
export const fromOrdinal2map = (ordinal: number) => {
    let mapProps: MapProps | null = null;
    for (const key in mapTabelle) {
        if (mapTabelle[key as IdTabellaGenericaAzienda].ordinal === ordinal) {
            mapProps = mapTabelle[key as IdTabellaGenericaAzienda];
        }
    }
    if (!mapProps)
        throw new Error('Tabella non trovata!');
    return mapProps;
}

export interface TabellaAziendaParams extends AziendaParams {
    idTabella: IdTabellaGenericaAzienda
}

interface ElementoTabellaAziendaParams extends TabellaAziendaParams {
    codiceElemento: string
}

interface TabellaSpecificaAziendaParams extends AziendaParams {
    idTabella: number
}

interface RegolaGiustAutoParams extends TabellaSpecificaAziendaParams {
    idRegola: number
}

export const listAccumuli = (params: AziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/accumulo?${addQueryParams(queryParams)}`
    return axios<AccumuloRecord[]>(url).then(res => res.data)
}

export const listGiustificativi = (params: AziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/giustificativo?${addQueryParams(queryParams)}`
    return axios<GiustificativoRecord[]>(url).then(res => res.data)
}

export const listValoriTabellaAzienda = (params: TabellaAziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/tabella/${params.idTabella}?${addQueryParams(queryParams)}`
    return axios<CentroDiCostoRecord[]>(url).then(res => res.data)
}

export const listGiustificativiXStatus = (params: ElementoTabellaAziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/tabella/status/${params.codiceElemento}/giustificativo?${addQueryParams(queryParams)}`
    return axios<StatusGiustificativoRecord[]>(url).then(res => res.data)
}

export const listTurni = (params: AziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/turno?${addQueryParams(queryParams)}`
    return axios<TurnoRecord[]>(url).then(res => res.data)
}

interface TurnoParams extends AziendaParams {
    codTurno: number
}

export const getTurno = (params: TurnoParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/turno/${params.codTurno}?${addQueryParams(queryParams)}`
    return axios<TurnoRecord>(url).then(res => res.data)
}

/**
 * ARROTONDAMENTI: LISTA, GET
 * RANGE ARROTONDAMENTI: LISTA
 */
interface ArrotondamentiParams extends AziendaParams {
    idArrotondamenti: number
}

export const listTabelleArrotondamento = (params: AziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/arrotondamenti?${addQueryParams(queryParams)}`
    return axios<TabellaArrotondamentiRecord>(url).then(res => res.data)
}
export const getTabellaArrotondamento = (params: ArrotondamentiParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/arrotondamenti/${params.idArrotondamenti}?${addQueryParams(queryParams)}`
    return axios<TabellaArrotondamentiRecord>(url).then(res => res.data)
}
export const listRangeArrotondamenti = (params: ArrotondamentiParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/arrotondamenti/${params.idArrotondamenti}/range?${addQueryParams(queryParams)}`
    return axios<ArrotondamentiRangeRecord[]>(url).then(res => res.data)
}
export const listTabelleFasceOrarie = (params: AziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/tabella/fasciaOraria?${addQueryParams(queryParams)}`
    return axios<FasciaOrariaWeekRecord[]>(url).then(res => res.data)
}
export const getTabellaFasceOrarie = (params: TabellaSpecificaAziendaParams) => {
    const queryParams: string[] = addBaseQueryParams(params);
    const url = config.getBaseUrl('FPNet') + `/azienda/${params.idAziendaTabelle}/tabella/fasciaOraria/${params.idTabella}?${addQueryParams(queryParams)}`
    return axios<FasciaOrariaWeekRecord>(url).then(res => res.data)
}
